import React, { useState, useEffect } from 'react';
import { useTranslations } from 'use-intl';
import InputField from '@/UIlibrary/InputField/InputField';
import Button from '@/UIlibrary/Button/Button';
import Modal from '@/stories/Library/Components/Modal/Modal';
import { Typography } from '@/stories/Library/Components/Typography/Typography';
import { BRAND_COLORS } from '@/stories/Library/Components/globalTheme';

interface UpdateCompanyFormProps {
	editing: boolean;
	setEditing: React.Dispatch<React.SetStateAction<boolean>>;
	onUpdateInformationSuccess: () => void;
	onUpdateInformationFailure: () => void;
}

// 4MB in bytes
export const LOGO_URL_MAX_SIZE = 4 * 1024 * 1024;

export const UpdateCompanyForm: React.FC<UpdateCompanyFormProps> = ({
	editing,
	setEditing,
	onUpdateInformationSuccess,
	onUpdateInformationFailure
}) => {
	const t = useTranslations('Information');
	const [formData, setFormData] = useState({
		businessName: 'Fincluyente',
		tradeName: 'Fincluyente',
		taxId: 'RFC',
		logo: new File([], '')
	});
	const [logoURL, setLogoURL] = useState<string>('/logo.svg');
	const [loading, setLoading] = useState(false);
	const [formIsValid, setFormIsValid] = useState(false);
	const [showImagePreviewModal, setShowImagePreviewModal] = useState(false);
	const [fileError, setFileError] = useState<boolean>(false);

	useEffect(() => {
		const noEmptyFields = Object.values(formData).every((value) => value !== '');
		setFormIsValid(noEmptyFields);
	}, [formData]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormData({
			...formData,
			[e.target.name]: e.target.value
		});
	};

	const handleSubmitRegistration = async (e?: React.FormEvent<HTMLFormElement>) => {
		if (e) e.preventDefault();
		setLoading(true);
		console.log('make request to update company');
		onUpdateInformationSuccess();
		onUpdateInformationFailure();
		setLoading(false);
	};

	const handleCancelEdit = () => {
		setEditing(false);
		setFormData({
			businessName: 'Fincluyente',
			tradeName: 'Fincluyente',
			taxId: 'RFC',
			logo: new File([], '')
		});
		setFileError(false);
		setLogoURL('/logo.svg');
	};

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFileError(false);

		if (e.target.files && e.target.files[0]) {
			const file = e.target.files[0];
			if (file.size > LOGO_URL_MAX_SIZE) {
				setFileError(true);
				return;
			}
			setLogoURL(URL.createObjectURL(file));
			setFormData({ ...formData, logo: file });
			setShowImagePreviewModal(true);
		}
	};

	const handleConfirmImage = () => {
		setShowImagePreviewModal(false);
		handleSubmitRegistration();
	};

	const ImagePreviewModal = () => (
		<Modal
			isOpen={showImagePreviewModal}
			maxWidth={400}
			onClose={() => setShowImagePreviewModal(false)}
			closeable={false}
		>
			<div>
				<Typography size="md" weight="semibold">
					{t('PreviewLogo')}
				</Typography>
			</div>
			<Typography size="sm" weight="regular" color="subtitle">
				{t('PreviewDisclaimer')}
			</Typography>
			<div className="flex justify-center mt-6">
				<div className="w-[125px] h-[65px]">
					<img
						src={logoURL}
						alt="Preview logo"
						className="w-auto h-auto max-w-[125px] max-h-[65px] object-contain"
						width={150}
						height={65}
					/>
				</div>
			</div>
			<div className="flex justify-between mt-8">
				<Button
					label={t('Cancel')}
					color="secondary"
					size="medium"
					onClick={() => setShowImagePreviewModal(false)}
				/>
				<Button
					dataTestId="confirmLogoPreviewBtn"
					label={t('Confirm')}
					color="primary"
					size="medium"
					onClick={handleConfirmImage}
				/>
			</div>
		</Modal>
	);

	return (
		<>
			<form onSubmit={handleSubmitRegistration}>
				<div className="flex flex-col gap-3">
					<div className="flex flex-col gap-2 flex-wrap">
						<div className="relative w-3/4 md:w-1/2">
							<div className="flex justify-start md:justify-center mt-4">
								<div className="w-[160px] h-[80px]">
									<img
										src={logoURL}
										alt="Preview logo"
										className="w-auto h-auto max-w-[90px] max-h-[65px] md:max-w-[160px] md:max-h-[80px] object-contain"
										width={150}
										height={65}
									/>
								</div>
							</div>
						</div>
						{editing && (
							<>
								<label className="block">
									<span className="sr-only">Choose company logo</span>
									{fileError && (
										<div data-testid="logoErrorMessage">
											<Typography size="sm" color="error">
												{t('FileTooBigError')}
											</Typography>
										</div>
									)}
									<div>
										<Typography size="sm" color="subtitle">
											{t('LogoSpecs')}
										</Typography>
									</div>
									<input
										type="file"
										className={`block w-full text-sm text-slate-500
                                file:mr-4 file:py-2 file:px-4
                                file:rounded-full file:border-0
                                file:text-sm file:font-semibold
                                file:bg-[${BRAND_COLORS.brand50}] file:text-[${BRAND_COLORS.brand600}]
                                hover:file:bg-[${BRAND_COLORS.brand100}]
                                `}
										accept="image/png, image/jpeg, image/jpg, image/webp"
										onChange={handleImageChange}
									/>
								</label>
							</>
						)}
					</div>
					<InputField
						dataTestId="businessNameInput"
						name="businessName"
						label={t('CommercialName')}
						value={formData.businessName}
						onChange={handleChange}
						loading={loading}
						disabled={loading || !editing}
						placeholder={t('CommercialName')}
						size={'auto'}
					/>
					<InputField
						dataTestId="tradeNameInput"
						name="tradeName"
						label={t('TradeName')}
						value={formData.tradeName}
						onChange={handleChange}
						loading={loading}
						disabled={loading || !editing}
						placeholder={t('TradeName')}
						size={'auto'}
					/>
					<InputField
						dataTestId="taxIdInput"
						name="taxId"
						label={t('RFC')}
						value={formData.taxId}
						onChange={handleChange}
						loading={loading}
						disabled={loading || !editing}
						placeholder={t('RFC')}
						size={'auto'}
					/>
					{editing && (
						<div className="flex flex-row items-center justify-around ">
							<Button
								label={t('Cancel')}
								color="secondary"
								size="medium"
								onClick={handleCancelEdit}
							/>
							<Button
								dataTestId="saveChangesBtn"
								disabled={!formIsValid || loading || fileError}
								label={t('Save')}
								type="submit"
								size="auto"
							/>
						</div>
					)}
				</div>
			</form>
			<ImagePreviewModal />
		</>
	);
};
